import { useCallback, useEffect, useRef } from 'react';

import ArrowRightIcon from '@heroicons/react/24/outline/ArrowRightIcon';
import CheckCircleIcon from '@heroicons/react/24/outline/CheckCircleIcon';
import { Button, Card, CardBody, Icon, Text } from 'atp-react-ui';

import { ROUTES } from '@/_navigation/routes';
import { useDataContext } from '@/_provider';
import FinishFaster from '@/_static/assets/finishFasterNew.svg?react';
import Only19 from '@/_static/assets/only19_1.svg?react';
import { getFormData, updateLocalStorage } from '@/_utils';

import { DangerousText } from '../DangerousText';

export function PageCard({
  navigate,
  buttonIsLoading,
  leadObj,
  currentPage,
  submitLeadQualification,
  handleGoogleEvent,
  isPending,
}) {
  const buttonRef = useRef(null);
  const { currentObj, isLoading, setIsLoading, updateCategory, dev_setPortal, isValidTimeToMeet } = useDataContext();

  const handleScheduleMeeting = useCallback(() => {
    if (isLoading) return;

    updateCategory(currentObj?.secondaryCategory);
    const val = 'I prefer to schedule at another time.';
    handleGoogleEvent('no_pre_buy_calendly', true);
    updateLocalStorage('formData', { ATP_14__c: val });
    submitLeadQualification(false, () => {
      handleGoogleEvent('reservation_confirmed');
      handleGoogleEvent('ca');
      setIsLoading(false);
      navigate(ROUTES.BRIDGE_PLAN_PRE_BUY_RES_SCHEDULED.link);
    });
  }, [
    currentObj?.secondaryCategory,
    handleGoogleEvent,
    isLoading,
    navigate,
    setIsLoading,
    submitLeadQualification,
    updateCategory,
  ]);

  const handleClick = useCallback(
    (e, meetnow = false) => {
      e.preventDefault();

      if (currentObj?.customKey === 'bpPreBuyReservation') {
        if (meetnow) {
          if (!isValidTimeToMeet) {
            handleScheduleMeeting();
            return;
          }

          const val = 'I prefer to meet with an advisor now.';
          updateCategory(currentObj?.category);
          handleGoogleEvent('no_pre_buy_calendly', true);
          updateLocalStorage('formData', { ATP_14__c: val });
          updateLocalStorage('ui', { currentOptionValue: val });

          submitLeadQualification(false, () => {
            setIsLoading(false);
            handleGoogleEvent('sn');
            navigate(`${ROUTES.REDIRECT_TO_MEET_NOW.onlyLink}/${leadObj.token__c}`, {
              state: { from: currentPage, dev_setPortal },
            });
          });
        } else {
          setIsLoading(true);

          if (!isValidTimeToMeet) {
            updateCategory(currentObj?.secondaryCategoryOfHours);
            submitLeadQualification(false, () => {
              setIsLoading(false);
              window.open('https://www.achievetestprep.com/testimonials');
            });
            return;
          }

          handleScheduleMeeting();
        }
      } else if (
        (currentObj?.customKey === 'whyShouldBridge' || currentObj?.customKey === 'prebuyyourbridge') &&
        meetnow
      ) {
        setIsLoading(true);
        updateCategory(`${currentObj?.category}${currentObj?.customKey === 'prebuyyourbridge' ? 'Yes' : ''}`);
        if (currentObj?.customKey === 'prebuyyourbridge') {
          handleGoogleEvent('pre_buy', true);
        }
        submitLeadQualification(false, () => {
          setIsLoading(false);
          navigate(currentObj?.nextPage);
        });
      } else {
        navigate(currentObj?.nextPage);
      }
    },
    [
      currentObj?.category,
      currentObj?.customKey,
      currentObj?.nextPage,
      currentObj?.secondaryCategoryOfHours,
      currentPage,
      dev_setPortal,
      handleGoogleEvent,
      handleScheduleMeeting,
      isValidTimeToMeet,
      leadObj.token__c,
      navigate,
      setIsLoading,
      submitLeadQualification,
      updateCategory,
    ]
  );

  const handleRenderHTML = useCallback((key) => {
    switch (key) {
      case 'bpPreBuyReservation':
        return <BpPreBuyReservation />;
      case 'whyShouldBridge':
        return <WhyShouldBridge />;
      case 'planFinishFaster':
        return <PlanFinishFaster />;
      case 'youAndYourAdvisor':
        return <YouAndYourAdvisor />;
      case 'prebuyyourbridge':
        return <GetYourBridgePlan />;
      default:
        return null;
    }
  }, []);

  const handleContinueWOSavingsGA = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);
    updateCategory(`${currentObj?.category}No`);
    submitLeadQualification(false, () => {
      setIsLoading(false);
      handleGoogleEvent('no_pre_buy', true);

      /** *** // !important 06/10/22 */
      navigate(ROUTES.BRIDGE_PLAN_WO_PRE_BUY_READY.link);
      /** *** // !important 06/10/22 */
    });
  }, [
    currentObj?.category,
    handleGoogleEvent,
    isLoading,
    navigate,
    setIsLoading,
    submitLeadQualification,
    updateCategory,
  ]);

  const handleContinueLink = useCallback(() => {
    switch (currentObj?.customKey) {
      case 'prebuyyourbridge':
        return (
          <div className="w-full">
            <Button
              theme="primary"
              as="link"
              className="mt-1 underline focus:ring-0 disabled:border-primary-light disabled:bg-primary-light disabled:text-primary-medium"
              onClick={handleContinueWOSavingsGA}
            >
              Continue without a plan
            </Button>
          </div>
        );

      case 'bpPreBuyReservation':
        return (
          <div className="w-full">
            <Button
              theme="primary"
              as="link"
              className="min-w-48 rounded-none text-xl font-bold disabled:border-primary-light disabled:bg-primary-light disabled:text-primary-medium"
              onClick={handleClick}
            >
              {isValidTimeToMeet ? 'Schedule a time' : 'How our students succeed'}
            </Button>
          </div>
        );
      default:
        return <div />;
    }
  }, [currentObj?.customKey, handleClick, handleContinueWOSavingsGA, isValidTimeToMeet]);

  const continueLink = handleContinueLink();
  const renderHTML = handleRenderHTML(currentObj?.customKey);

  return (
    <Card className="shadow-card-primary-light">
      <CardBody>
        {renderHTML}

        {!currentObj?.hideButton && (
          <div className="mt-8 flex w-full flex-col items-center justify-center">
            <Button
              ref={buttonRef}
              theme="primary"
              size="lg"
              className={
                'min-w-48 rounded-none text-xl font-bold disabled:border-primary-light disabled:bg-primary-light disabled:text-primary-medium'
              }
              onClick={(e) => {
                handleClick(e, true, currentObj?.customKey === 'immediateCallChat');
              }}
              showLoader={!(!buttonIsLoading && !isLoading) || isPending}
              iconRight={currentObj?.hideButtonIcon || isPending ? null : ArrowRightIcon}
            >
              {isPending
                ? 'Please wait...'
                : currentObj?.customKey === 'bpPreBuyReservation' && !isValidTimeToMeet
                  ? 'Schedule Now'
                  : currentObj.buttonText}
            </Button>

            <div className="mb-5 mt-2">{continueLink}</div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function BpPreBuyReservation() {
  const { isValidTimeToMeet } = useDataContext();

  if (!isValidTimeToMeet) {
    return (
      <>
        <Text as="h1" className="mb-4 mt-8 text-center font-serif font-semibold">
          That’s great, Name! Now, let’s explore RN College Bridge options tailored just for you...
        </Text>
        <Text as="caption-medium" className="py-4 text-center text-xl text-accent-medium">
          Schedule a dedicated time for our Education Advisors to focus on you and your RN goals.
        </Text>
      </>
    );
  }

  return (
    <>
      <Text as="h1" className="mb-4 mt-8 text-center font-serif font-semibold">
        Our Advisors customize every Bridge Plan after assessing individual goals.{' '}
      </Text>
      <Text as="caption-medium" className="py-4 text-center text-xl text-accent-medium">
        Do you have time to start a call now? Our Advisors are ready to talk when you are.
      </Text>
    </>
  );
}

function WhyShouldBridge() {
  const quote = `"A goal without a plan is just a wish."`;
  const text = `Yes! This research would take significant time and effort on your own. Let our experts do the work to map out exactly which courses to take, when to take them, and how much they’ll cost.`;
  return (
    <>
      <Text as="h1" className="mb-4 mt-8 text-center font-serif font-semibold text-secondary-dark">
        Should you get a College Bridge Plan?
      </Text>
      <Text as="caption-medium" className="py-4 text-center text-xl text-secondary-dark">
        {text}
      </Text>
      <Text as="caption" className="text-center text-base font-medium italic text-secondary">
        {quote}
      </Text>
    </>
  );
}

function PlanFinishFaster() {
  return (
    <>
      <div className="mt-8 flex items-center justify-center">
        <FinishFaster className="h-24" />
      </div>
      <Text as="h1" className="mb-4 mt-6 text-center font-serif font-semibold text-secondary-dark">
        Take the <span className="bold italic">next step</span>
      </Text>
      <Text as="caption-medium" className="py-4 text-center text-xl text-secondary-dark">
        We’re ready to personalize a College Bridge Plan just for you. This detailed, 3-page document puts your RN
        future into focus.
      </Text>
    </>
  );
}

function GetYourBridgePlan() {
  useEffect(() => {
    const formData = getFormData() ?? {};
    delete formData.MQL_Readiness__c;
    delete formData.Readiness_Option_1__c;
    delete formData.Readiness_Option_2__c;
    delete formData.Readiness_Option_3__c;
    delete formData.Readiness_Option_4__c;
    delete formData.Readiness_Option_5__c;
    delete formData.ATP_14__c;

    localStorage.setItem('formData', JSON.stringify(formData));
  }, []);

  return (
    <>
      <Text as="h1" className="mb-4 mt-8 text-center font-serif font-semibold text-secondary-dark">
        Kickstart your RN goals with a custom College Bridge Plan.
      </Text>
      <div className="flex items-center justify-center">
        <Only19 className="my-5" />
      </div>
      <div className="mt-4 text-center text-sm italic tracking-wide text-secondary-dark">
        Receive a step-by-step plan tailored to your career goals and schedule.
      </div>
    </>
  );
}

function YouAndYourAdvisor() {
  const renderHtml = () => {
    const html = [
      {
        id: 1,
        icon: <Icon icon={CheckCircleIcon} className="h-8 w-8 text-primary-medium" />,
        body: 'Finds the right accredited nursing college <div class="i">for you</div>.',
      },
      {
        id: 2,
        icon: <Icon icon={CheckCircleIcon} className="h-8 w-8 text-primary-medium" />,
        body: 'Confirms which courses you can fast track.',
      },
      {
        id: 3,
        icon: <Icon icon={CheckCircleIcon} className="h-8 w-8 text-primary-medium" />,
        body: 'Verifies transferable credits to your accredited college.',
      },
    ];

    return (
      <div className="w-full px-10">
        <section className={`mt-8 flex flex-col`}>
          {html.map((obj) => {
            return (
              <div className="my-2 flex items-center" key={obj.id}>
                <div>
                  <Icon icon={CheckCircleIcon} className="h-8 w-8 text-primary-medium" />
                </div>
                <div className="pl-4 text-base text-secondary-dark">
                  <DangerousText>{obj.body}</DangerousText>
                </div>
              </div>
            );
          })}
        </section>
      </div>
    );
  };

  return (
    <>
      <Text as="h1" className="mb-4 mt-8 text-center font-serif font-semibold text-secondary-dark">
        Get a tailored College Bridge Plan that:
      </Text>
      {renderHtml()}
    </>
  );
}
